import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import {
  AreaChart, Area, BarChart, Bar, ComposedChart,
  ScatterChart, Scatter, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ZAxis
} from "recharts";
import { useWell } from "../WellContext";          
import { useAIAdvice } from "../useAIAdvice";
import { useTheme, getColors } from "../ThemeContext";  
import PlotSummary from "../components/PlotSummary";

const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }   
/* ───────────── TIME-WINDOW HELPERS (same bar / axis / look as Overview, AI Controller, GLR) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

/* Apply a window to ANY array of rows that carry `_t` (ms). Presets are measured back from the LATEST cycle in the data, not from "now". */
function applyWin(w, tMax, arr) {
  if (w.mode === "all" || tMax == null) return arr;
  if (w.mode === "cyc30") {
    if (arr.some(r => r.well != null)) {            // multi-well rows -> newest 30 of EACH well
      const by = {};
      arr.forEach(r => { (by[r.well] = by[r.well] || []).push(r); });
      return Object.values(by).flatMap(a => a.slice(-30));
    }
    return arr.slice(-30);
  }
  if (w.mode === "custom") {
    const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
    const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
    return arr.filter(d => d._t != null && d._t >= f && d._t <= t);
  }
  const p = PRESETS.find(x => x.id === w.mode);
  return arr.filter(d => d._t != null && d._t >= tMax - p.ms);
}
/* `rows` = every cycle time the window bar should know about ({_t, well?}). `filter(arr)` windows any per-well / per-view array the same way. */
function useTimeWindow(rows) {
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  const tMin = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [rows]);
  const tMax = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [rows]);
  const view = useMemo(() => applyWin(win, tMax, rows), [rows, win, tMax]);
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, applyWin({ mode: p.id }, tMax, rows).length])), [rows, tMax]);
  return { win, setWin, view, presetCounts, tMin, tMax, isAll: win.mode === "all", filter: arr => applyWin(win, tMax, arr) };
}


/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `key` (default "index") and `_t`. labelText = the "Cycle Number →" caption, moved under the timestamp row. */
function dateXAxis(rows, key = "index", nTicks = 5, labelText, opts = {}) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => stamped[i]).map(i => stamped[i][key]);
  const lookup = new Map(stamped.map(r => [r[key], r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey={key} ticks={ticks} interval={0} tickLine={false} axisLine={false} height={opts.height || 32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={labelText ? { value: labelText, position: "insideBottom", offset: opts.labelOffset ?? -14, fill: C.muted, fontSize: 9, fontFamily: MONO } : undefined} />
  );
}
const hasStamps = rows => rows.some(r => r._t != null);

function DataWindowBar({ ctl, total, note, isDark }) {
  const { win, setWin, view, presetCounts, tMin, tMax, isAll } = ctl;
  const vs      = view.filter(d => d._t != null);
  const vStart  = vs.length ? Math.min(...vs.map(d => d._t)) : null;
  const vEnd    = vs.length ? Math.max(...vs.map(d => d._t)) : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {total} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window, too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range, widen the window to see a trend.
        </div>
      )}
    </div>
  );
}


const vals = (rows, k) => rows.map(r => r[k]).filter(v => typeof v === "number" && isFinite(v));
function statsOf(rows, k) {      // same mean / max / median the backend col_stats returns, on the cycles inside the window
  const v = vals(rows, k);
  if (!v.length) return undefined;
  const s = [...v].sort((a, b) => a - b), n = s.length;
  return { mean: v.reduce((a, b) => a + b, 0) / n, max: s[n - 1], min: s[0], median: n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2 };
}

/* window-aware version of /afterflow/ratio-analysis (same 20 equal-width bins + thresholds as the backend) */
function ratioOf(rows, cyc) {
  let r = vals(rows, "AfterFlowRatio");
  if (!r.length) r = cyc.map(c => c.afterflow_pct).filter(v => v != null && isFinite(v)).map(v => v / 100);
  if (!r.length) return null;
  const n = r.length, mean = r.reduce((a, b) => a + b, 0) / n, srt = [...r].sort((a, b) => a - b);
  const median = n % 2 ? srt[(n - 1) / 2] : (srt[n / 2 - 1] + srt[n / 2]) / 2;
  const lo = srt[0], hi = srt[n - 1], N = 20, W = (hi > lo ? hi - lo : 1) / N;
  const counts = new Array(N).fill(0);
  r.forEach(x => { let k = Math.floor((x - lo) / W); if (k >= N) k = N - 1; counts[k]++; });
  return {
    bins: Array.from({ length: N }, (_, k) => +(lo + k * W).toFixed(3)), counts,
    mean: +mean.toFixed(3), median: +median.toFixed(3),
    low_afterflow_count: r.filter(x => x < 0.3).length,
    balanced_count: r.filter(x => x >= 0.3 && x <= 0.7).length,
    high_afterflow_count: r.filter(x => x > 0.7).length,
    total: n,
  };
}

const CHART_MARGIN_DEFAULT = { top:8, right:14, left:-10, bottom:36 };
const CHART_MARGIN_DUAL_Y  = { top:8, right:14, left:-10, bottom:36 };
const CHART_MARGIN_HISTO   = { top:8, right:14, left:-10, bottom:36 };
const CHART_MARGIN_SCATTER = { top:8, right:14, left:-10, bottom:36 };

const LEGEND_PROPS = {
  wrapperStyle:{ fontFamily:MONO, fontSize:10, paddingBottom:4 },
  verticalAlign:"top", align:"right", iconSize:8,
};

function cycleTickFmt(v) {
  if(v==null||v==="") return ""; const n=Number(v); if(!isNaN(n)) return `C${n}`; return String(v);
}
function CYCLE_XAXIS_PROPS() {
  return { tick:{fill:C.muted,fontSize:9,fontFamily:MONO}, tickLine:false, tickFormatter:cycleTickFmt };
}

function AnimNum({ to, dec=1, suffix="" }) {
  if(to==null||isNaN(Number(to))) return <span>-</span>;
  return <span>{Number(to).toFixed(dec)}{suffix}</span>;
}

function StatCard({ label, value, unit, color, sub, icon, delay=0 }) {
  return (
    <div className="card-hover" style={{
      background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${color}`,
      borderRadius:12, padding:"18px 20px",
      animation:`fade-up 0.5s ease ${delay}s both`, position:"relative", overflow:"hidden"
    }}>
      <div style={{ fontSize:18, marginBottom:6 }}>{icon}</div>
      <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4 }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:24, fontWeight:700, color, lineHeight:1 }}>
        <AnimNum to={value} dec={1} suffix={unit}/>
      </div>
      {sub && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:5 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, height=260, delay=0, accentColor, summaryTitle, summarySubtitle }) {
  const ac=accentColor||C.cyan;
  return (
    <div style={{
      background:C.card, border:`1px solid ${C.border}`, borderRadius:14,
      padding:"20px 16px 20px", animation:`fade-up 0.5s ease ${delay}s both`,
      position:"relative", overflow:"visible"
    }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${ac}88, transparent)` }}/>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{subtitle}</div>}
      </div>
      <div style={{ height }}>{children}</div>
      <div style={{ marginTop:10 }}>
        <PlotSummary inline title={summaryTitle||title} subtitle={summarySubtitle||(typeof subtitle==="string"?subtitle:"")} accent={ac}>{children}</PlotSummary>
      </div>
    </div>
  );
}

function InfoRow({ label, value, color=C.cyan, unit="" }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${C.border}` }}>
      <span style={{ fontFamily:MONO, fontSize:11, color:C.muted }}>{label}</span>
      <span style={{ fontFamily:MONO, fontSize:11, color }}>{value!=null?`${value} ${unit}`.trim():"-"}</span>
    </div>
  );
}

function renderAdvice(text) {
  if(!text) return null;
  const ok  = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, marginTop:1 }}><circle cx="7" cy="7" r="6" stroke={C.green} strokeWidth="1.5" fill={`${C.green}22`}/><path d="M4.3 7.2L6.2 9.1L9.8 5.3" stroke={C.green} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  const warn = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, marginTop:1 }}><path d="M7 1.5L13 12.5H1L7 1.5Z" stroke={C.amber} strokeWidth="1.5" fill={`${C.amber}22`} strokeLinejoin="round"/><line x1="7" y1="5" x2="7" y2="8.3" stroke={C.amber} strokeWidth="1.5" strokeLinecap="round"/><circle cx="7" cy="10.2" r="0.9" fill={C.amber}/></svg>;
  const bad = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, marginTop:1 }}><circle cx="7" cy="7" r="6" stroke={C.red} strokeWidth="1.5" fill={`${C.red}22`}/><path d="M4.8 4.8L9.2 9.2M9.2 4.8L4.8 9.2" stroke={C.red} strokeWidth="1.5" strokeLinecap="round"/></svg>;
  const ICONS = { "◆":ok, "▲":warn, "▶":warn, "■":bad, "▽":bad };
  for(const [sym, icon] of Object.entries(ICONS)) {
    if(text.startsWith(sym+" ")) {
      return <span style={{ display:"flex", alignItems:"flex-start", gap:6, lineHeight:1.5 }}>{icon}<span>{text.slice(sym.length+1)}</span></span>;
    }
  }
  return <span>{text}</span>;
}

function DonutChart({ data, colors, title, subtitle, fieldKey, summarySubtitle }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [pos, setPos] = useState(null);
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKeyRef = useRef(null);

  function placeTip(e) {
    const TW=320, TH=260;
    const x=e.clientX+18, y=e.clientY+14;
    setPos({ x:x+TW>window.innerWidth?Math.max(8,e.clientX-TW-18):x, y:Math.max(8,y+TH>window.innerHeight?window.innerHeight-TH-16:y) });
  }
  function handleMouseEnter(entry, index, e) {
    setActiveIndex(index);
    if(e && e.clientX!=null) placeTip(e);
    const total = data.reduce((a,d)=>a+(d.value||0),0);
    const share = v => total>0 ? Math.round(v/total*1000)/10 : null;
    const key = `${fieldKey||entry.name}_${entry.value}_${total}`;
    if(prevKeyRef.current===key) return;
    prevKeyRef.current=key;
    const allFields = data.map(d=>({ field:d.name, label:`${d.name} - cycles in this band`, value:d.value, unit:`cycles (${share(d.value)}% of ${total} cycles)` }));
    fetchAdvice(fieldKey||entry.name, entry.value, `cycles (${share(entry.value)}% of ${total} cycles in the window; ${title} compares all bands)`, null, { name:entry.name, value:entry.value, total_cycles:total, share_pct:share(entry.value) }, allFields);
  }
  function handleMouseLeave() { setActiveIndex(null); setPos(null); prevKeyRef.current=null; clearAdvice(); }

  const tip = (activeIndex!==null && pos && data[activeIndex]) ? (() => {
    const entry=data[activeIndex];
    const total=data.reduce((s,d)=>s+(d.value||0),0);
    const pct=total>0?((entry.value/total)*100).toFixed(1):"-";
    const ac = (!advice||loading) ? C.muted : advice.startsWith("■")||advice.startsWith("▽") ? C.red : advice.startsWith("▲")||advice.startsWith("▶") ? C.amber : C.green;
    return createPortal(
      <div style={{ position:"fixed", left:pos.x, top:pos.y, width:320, background:C.card, border:`1px solid ${ac}44`, borderLeft:`4px solid ${ac}`, borderRadius:12, padding:"12px 16px", fontFamily:MONO, fontSize:11, boxShadow:"0 12px 40px #000c", zIndex:999999, pointerEvents:"none" }}>
        <div style={{ borderBottom:`1px solid ${C.border}`, paddingBottom:7, marginBottom:8 }}>
          <span style={{ color:C.text, fontWeight:700, fontSize:12 }}>{entry.name}</span>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
          <span style={{ color:C.muted, fontSize:10 }}>Cycles</span>
          <span style={{ color:colors[activeIndex]||C.cyan, fontWeight:700, fontSize:13 }}>{entry.value}</span>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
          <span style={{ color:C.muted, fontSize:10 }}>Share</span>
          <span style={{ color:C.text, fontSize:11 }}>{pct}%</span>
        </div>
        <div style={{ marginTop:4, borderTop:`1px solid ${C.border}`, paddingTop:8, fontSize:10, lineHeight:1.6, background:loading?`${C.muted}08`:`${C.cyan}08`, borderRadius:6, padding:"6px 8px" }}>
          {loading ? <span style={{ color:C.muted }}>Getting AI recommendation...</span>
                   : advice ? <span style={{ color:ac }}>{renderAdvice(advice)}</span>
                   : <span style={{ color:C.muted }}>Hover a slice for AI advice</span>}
        </div>
      </div>, document.body);
  })() : null;

  const pie = (
    <PieChart width={160} height={160}>

        <Pie data={data} cx={80} cy={80} innerRadius={45} outerRadius={72} dataKey="value" paddingAngle={2}

          onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>

          {data.map((entry,i)=>(

            <Cell key={i} fill={colors[i]} stroke={activeIndex===i?"#fff":"none"} strokeWidth={activeIndex===i?2:0}

              opacity={activeIndex===null||activeIndex===i?1:0.55} style={{ cursor:"pointer" }}/>

          ))}

        </Pie>

    </PieChart>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
      <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
      <div onMouseMove={placeTip} onMouseLeave={handleMouseLeave}>{pie}</div>
      {tip}
      <div style={{ display:"flex", flexDirection:"column", gap:6, width:"100%" }}>
        {data.map((d,i)=>(
          <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:activeIndex===i?`${colors[i]}18`:"transparent", borderRadius:6, padding:"3px 6px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:8, height:8, borderRadius:2, background:colors[i] }}/>
              <span style={{ fontFamily:MONO, fontSize:10, color:activeIndex===i?C.text:C.muted }}>{d.name}</span>
            </div>
            <span style={{ fontFamily:MONO, fontSize:10, color:activeIndex===i?colors[i]:C.muted, fontWeight:activeIndex===i?700:400 }}>{d.value}</span>
          </div>
        ))}
      </div>
      <div style={{ width:"100%" }}>
        <PlotSummary inline title={title} subtitle={summarySubtitle||""} accent={C.purple}>{pie}</PlotSummary>
      </div>
      {subtitle && <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, textAlign:"center" }}>{subtitle}</div>}
    </div>
  );
}

/* ───────────── AFTER-FLOW DEPLETION & ECONOMIC CLOSE (estimated from cycle records) ───────────── */
const AF_VERDICT = {
  AFTERFLOW_DEPLETED:    { label:"DEPLETED - BELOW BREAK-EVEN", color:"red",    action:"Begin the next cycle sooner" },
  AT_OPTIMAL_CLOSE:      { label:"CLOSED AT THE OPTIMUM",       color:"green",  action:"Closed near the economic optimum" },
  STABLE_PRODUCTIVE:     { label:"STABLE & PRODUCTIVE",         color:"teal",   action:"Keep flowing - flat at a high rate" },
  PRODUCTIVE_DECLINE:    { label:"DECLINING, STILL PAYING",     color:"cyan",   action:"Keep flowing until break-even" },
  DATA_INCONSISTENT:     { label:"RATE vs VOLUME MISMATCH",     color:"purple", action:"Check meter / volume" },
  INSUFFICIENT_BASELINE: { label:"NOT ENOUGH HISTORY",          color:"muted",  action:"Needs more prior cycles" },
};
const SEV_COLOR = { HIGH:"red", MEDIUM:"orange", LOW:"amber", GOOD:"green", INFO:"muted" };

function Tag({ color, children, title }) {
  return <span title={title} style={{ fontFamily:MONO, fontSize:10, letterSpacing:1, color, background:`${color}14`, border:`1px solid ${color}33`, borderRadius:5, padding:"2px 8px", whiteSpace:"nowrap", justifySelf:"start" }}>{children}</span>;
}
function KV({ k, v, color }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:12, padding:"6px 0", borderBottom:`1px solid ${C.border}` }}>
      <span style={{ fontFamily:MONO, fontSize:10, color:C.muted }}>{k}</span>
      <span style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:color||C.text, textAlign:"right" }}>{v}</span>
    </div>
  );
}
function MiniStat({ label, value, sub, color }) {
  return (
    <div style={{ background:`${color}0d`, border:`1px solid ${color}33`, borderRadius:10, padding:"12px 14px" }}>
      <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4 }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:20, fontWeight:700, color, lineHeight:1.1 }}>{value}</div>
      {sub && <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:4 }}>{sub}</div>}
    </div>
  );
}
const f0 = v => (v==null||!isFinite(v)) ? "-" : Number(v).toFixed(0);
const f1 = v => (v==null||!isFinite(v)) ? "-" : Number(v).toFixed(1);
const f2 = v => (v==null||!isFinite(v)) ? "-" : Number(v).toFixed(2);
const fin = v => typeof v==="number" && isFinite(v);

/* ───────────── HOVER AI RECOMMENDATION FOR OPPORTUNITY ROWS ───────────── */
function OppRow({ field, value, unit, cycle, context, labels, style, children }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const [pos, setPos]     = useState(null);
  const [asked, setAsked] = useState(false);
  const timer = useRef(null);
  const ctx = Object.fromEntries(Object.entries(context).filter(([,v])=>typeof v==="number"&&isFinite(v)));
  const fields = Object.entries(ctx).filter(([k])=>labels[k]).map(([k,v])=>({ field:k, label:labels[k][0], value:Math.round(v*100)/100, unit:labels[k][1] }));
  useEffect(()=>()=>clearTimeout(timer.current),[]);
  function onEnter() {
    setAsked(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(()=>{ setAsked(true); fetchAdvice(field, value, unit, cycle, ctx, fields); }, 300);
  }
  function onMove(e) {
    const TW=330, TH=200;
    const x=e.clientX+18, y=e.clientY+14;
    setPos({ x:x+TW>window.innerWidth?e.clientX-TW-18:x, y:y+TH>window.innerHeight?window.innerHeight-TH-16:y });
  }
  function onLeave() { clearTimeout(timer.current); setPos(null); setAsked(false); clearAdvice(); }
  const ac = !advice ? C.cyan : advice.startsWith("■")||advice.startsWith("▽") ? C.red : advice.startsWith("▲")||advice.startsWith("▶") ? C.amber : C.green;
  const text = advice ? advice.replace(/^[◆▲▶■▽]\s*/, "") : "";
  return (
    <div style={style} onMouseEnter={onEnter} onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
      {pos && createPortal(
        <div style={{ position:"fixed", left:pos.x, top:pos.y, zIndex:999999, pointerEvents:"none", width:320, background:C.card, border:`1px solid ${ac}44`, borderLeft:`4px solid ${ac}`, borderRadius:12, padding:"12px 16px", fontFamily:MONO, fontSize:10, lineHeight:1.6, boxShadow:"0 12px 40px rgba(0,0,0,0.8)" }}>
          <div style={{ color:C.cyan, fontWeight:700, fontSize:12, marginBottom:6 }}>Cycle {cycle}</div>
          {(!asked||loading) ? <span style={{ color:C.muted }}>Getting AI recommendation...</span>
            : advice ? <span style={{ color:ac }}>{text}</span>
            : <span style={{ color:C.muted }}>Hover a row for AI advice</span>}
        </div>, document.body)}
    </div>
  );
}

const AF_OPP_LABELS = {
  suggested_afterflow_lo: ["Suggested After-Flow Duration (shortest)", "minutes"],
  suggested_afterflow_hi: ["Suggested After-Flow Duration (break-even crossing)", "minutes"],
  excess_min:     ["Recoverable After-Flow", "minutes"],
  afterflow_min:  ["After-Flow Duration", "minutes"],
  q_close:        ["Closing Rate", "MCF/d"],
  q_recycle:      ["Break-Even Rate", "MCF/d"],
  q0:             ["Initial Stabilised Rate", "MCF/d"],
  q_vs_recycle:   ["Closing Rate ÷ Break-Even", "×"],
  remaining_pct:  ["Initial Rate Remaining", "%"],
  confidence_pct: ["Estimate Confidence", "%"],
};

/* Expandable row for the after-flow opportunities list (click to show / hide the factors behind the estimate; hover still gives the AI recommendation) */
function AfOppItem({ r, sc, horizon }) {
  const [open, setOpen] = useState(false);
  const GRID = "minmax(120px,1.1fr) 150px 190px 70px minmax(150px,1.6fr) 24px";
  const hasCut = fin(r.afterflow_min) && fin(r.excess_min);
  // Suggested after-flow DURATION (minutes): shortest (full upper-estimate cut) .. break-even crossing point. Backend sends it; fallback computes it.
  const minLo  = fin(r.suggested_afterflow_lo) ? r.suggested_afterflow_lo : (hasCut ? Math.max(0, r.afterflow_min - r.excess_min) : null);
  const minHi  = fin(r.suggested_afterflow_hi) ? r.suggested_afterflow_hi : (hasCut && fin(r.t_cross_min) && r.t_cross_min >= minLo && r.t_cross_min <= r.afterflow_min ? r.t_cross_min : null);
  // "Break-even crossed at" has no time when the rate never came down through break-even (it was already at/below it from the start)
  const noCrossTxt = (q0, qr) => (fin(q0) && fin(qr) && q0 <= qr)
    ? `Not crossed, rate was below break-even from the start (${f0(q0)} vs ${f0(qr)} MCF/d)`
    : (fin(qr) ? "Not crossed - no measurable decline" : "-");
  const rangeTxt = fin(minLo) ? (fin(minHi) && Math.round(minHi)!==Math.round(minLo) ? `${f0(minLo)}-${f0(minHi)} minutes` : `${f0(minLo)} minutes`) : "-";
  const facts = [
    fin(minLo) && ["Suggested after-flow duration (shortest to break-even crossing)", rangeTxt],
    fin(r.excess_min) && ["Time that can be saved (upper estimate)", `up to ${f0(r.excess_min)} minutes`],
    ["After-flow duration (actual)", fin(r.afterflow_min)?`${f1(r.afterflow_min)} minutes`:"-"],
    ["Rate at close", fin(r.q_close)?`${f0(r.q_close)} MCF/d`:"-"],
    ["Break-even rate (well's average cycle rate)", fin(r.q_recycle)?`${f0(r.q_recycle)} MCF/d`:"-"],
    ["Initial stabilised rate (Q0)", fin(r.q0)?`${f0(r.q0)} MCF/d`:"-"],
    ["Rate remaining", fin(r.remaining)?`${(r.remaining*100).toFixed(0)}%`:"-"],
    ["Close rate ÷ break-even", fin(r.q_vs_recycle)?`${r.q_vs_recycle.toFixed(2)}×`:"-"],
    ["Loss per 10 minutes at close", fin(r.loss_per_10min_pct)?`${f1(r.loss_per_10min_pct)}%`:"-"],
    [`Next ${f0(horizon)} minutes: keep vs recycle`, fin(r.v_continue_mcf)&&fin(r.v_recycle_mcf)?`${f2(r.v_continue_mcf)} vs ${f2(r.v_recycle_mcf)} MCF`:"-"],
    ["Break-even crossed at", fin(r.t_cross_min)?`${f0(r.t_cross_min)} minutes into after-flow`:noCrossTxt(r.q0, r.q_recycle)],
    ["Estimate confidence", fin(r.confidence)?`${(r.confidence*100).toFixed(0)}%`:"-"],
  ].filter(Boolean);
  return (
    <div style={{ borderTop:`1px solid ${C.border}` }}>
      <div onClick={()=>setOpen(o=>!o)}>
        <OppRow field="excess_min" value={r.excess_min} unit="minutes" cycle={r.index} labels={AF_OPP_LABELS}
          context={{ excess_min:r.excess_min, afterflow_min:r.afterflow_min, q_close:r.q_close, q_recycle:r.q_recycle, q0:r.q0, q_vs_recycle:r.q_vs_recycle, remaining_pct:r.remaining!=null?r.remaining*100:null, confidence_pct:r.confidence!=null?r.confidence*100:null }}
          style={{ display:"grid", gridTemplateColumns:GRID, gap:10, alignItems:"center", padding:"8px 0", cursor:"pointer" }}>
          <span style={{ fontFamily:MONO, fontSize:10, color:C.amber }}>{r._t!=null?fmtDT(r._t):"-"} · C{r.index}</span>
          <span style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:C.cyan }}>{fin(r.afterflow_min)?`${f0(r.afterflow_min)} minutes`:"-"}</span>
          <span style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:C.teal }}>{rangeTxt}</span>
          <Tag color={sc}>{r.severity}</Tag>
          <span style={{ fontFamily:MONO, fontSize:9, color:C.muted }}>closed at {f0(r.q_close)} vs break-even {f0(r.q_recycle)} MCF/d · conf {(r.confidence*100).toFixed(0)}%</span>
          <span style={{ fontFamily:MONO, fontSize:11, color:C.muted, textAlign:"right" }}>{open?"▲":"▼"}</span>
        </OppRow>
      </div>
      {open && (
        <div style={{ background:`${C.red}0d`, border:`1px solid ${C.red}33`, borderLeft:`3px solid ${C.red}`, borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(230px, 1fr))", gap:"4px 24px" }}>
            {facts.map(([k,val])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", gap:10, fontFamily:MONO, fontSize:10, padding:"3px 0", borderBottom:`1px solid ${C.border}` }}>
                <span style={{ color:C.muted }}>{k}</span><span style={{ color:C.text, fontWeight:700 }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AfterflowDepletionPanel({ rows, basis, params, TTip }) {
  if(!rows.length) return null;
  const scored   = rows.filter(r=>r.remaining!=null);
  const plotted  = rows.filter(r=>r.q_close!=null);
  const depTh    = (params?.depleted_below ?? 0.4);
  const depleted = scored.filter(r=>r.depleted).length;
  const mean     = scored.length ? scored.reduce((a,r)=>a+r.remaining,0)/scored.length : null;
  const cut      = rows.filter(r=>r.verdict==="AFTERFLOW_DEPLETED");
  const recov    = cut.reduce((a,r)=>a+(r.excess_min||0),0);
  const mismatch = rows.filter(r=>r.verdict==="DATA_INCONSISTENT").length;
  const keepers  = rows.filter(r=>r.depleted && (r.verdict==="PRODUCTIVE_DECLINE"||r.verdict==="STABLE_PRODUCTIVE")).length;
  const latest   = rows.filter(r=>r.verdict!=="NO_DATA").sort((a,b)=>((b._t??-1)-(a._t??-1))||(b.index-a.index))[0];
  const topOpp   = cut.slice().sort((a,b)=>(b.excess_min||0)-(a.excess_min||0)||(a.q_vs_recycle??9)-(b.q_vs_recycle??9)).slice(0,5);
  const hasT     = hasStamps(plotted);
  const cm       = hasT ? { ...CHART_MARGIN_DEFAULT, bottom:22 } : CHART_MARGIN_DEFAULT;
  const lv       = latest ? (AF_VERDICT[latest.verdict]||{ label:latest.verdict, color:"muted", action:"" }) : null;
  const lc       = lv ? C[lv.color] : C.muted;
  const remData  = scored.map(r=>({ ...r, remaining_pct: r.remaining*100 }));

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderLeft:`3px solid ${C.red}`, borderRadius:12, padding:"14px 18px", animation:"fade-up 0.5s ease 0.12s both" }}>
        <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:10 }}>
          <span style={{ fontFamily:SANS, fontWeight:700, fontSize:14, color:C.text }}>After-Flow Depletion &amp; Economic Close</span>
          <Tag color={C.amber} title={basis||""}>ESTIMATED FROM CYCLE RECORDS</Tag>
          <Tag color={C.cyan}>{scored.length} of {rows.length} cycles scored</Tag>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(190px, 1fr))", gap:12 }}>
        <MiniStat label="Rate left at close" value={mean!=null?`${(mean*100).toFixed(0)}%`:"-"} sub="avg share of initial after-flow rate" color={C.teal}/>
        <MiniStat label="Depleted cycles" value={`${depleted} of ${scored.length}`} sub={`< ${(depTh*100).toFixed(0)}% of initial rate left${keepers?` · ${keepers} still above break-even`:""}`} color={depleted?C.orange:C.green}/>
        <MiniStat label="Recyclable after-flow" value={`${f0(recov)} min`} sub={`${cut.length} cycle${cut.length===1?"":"s"} below break-even (capped 4 h each)`} color={cut.length?C.red:C.green}/>
        <MiniStat label="Rate / volume mismatch" value={`${mismatch}`} sub="cycles excluded - check meter" color={mismatch?C.purple:C.green}/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"minmax(300px, 1fr) 2fr", gap:20 }}>
        {latest && (
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${lc}`, borderRadius:14, padding:"18px 18px", animation:"fade-up 0.5s ease 0.15s both" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:10 }}>
              <span style={{ fontFamily:MONO, fontSize:9, letterSpacing:2, color:C.muted }}>LATEST CYCLE IN WINDOW</span>
              <span style={{ fontFamily:MONO, fontSize:10, color:C.amber }}>{latest._t!=null?fmtDT(latest._t):`Cycle ${latest.index}`}</span>
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center", marginBottom:10 }}>
              <Tag color={lc}>{lv.label}</Tag>
              <Tag color={C.muted} title="Confidence in this estimate: history depth, whether rate and volume agree, and how long the after-flow was">CONF {(latest.confidence*100).toFixed(0)}%</Tag>
              {latest.shape && <Tag color={C.blue}>{latest.shape}</Tag>}
            </div>
            <div style={{ fontFamily:SANS, fontWeight:700, fontSize:15, color:lc, marginBottom:8 }}>{lv.action}{latest.verdict==="AFTERFLOW_DEPLETED"&&latest.excess_min>0?` · ~${f0(latest.excess_min)} minutes earlier`:""}</div>
            <KV k={"After-flow (cycle #"+latest.index+")"} v={latest.afterflow_min!=null?`${f1(latest.afterflow_min)} minutes`:"-"}/>
            <KV k="Initial stabilised rate (Q0)" v={latest.q0!=null?`${f0(latest.q0)} MCF/d`:"-"}/>
            <KV k="Rate at close" v={latest.q_close!=null?`${f0(latest.q_close)} MCF/d`:"-"}/>
            <KV k="Rate remaining" v={latest.remaining!=null?`${(latest.remaining*100).toFixed(0)}%  (−${f0(latest.decline_pct)}%)`:"-"} color={latest.depleted?C.orange:C.text}/>
            <KV k="Loss per 10 minutes at close" v={latest.loss_per_10min_pct!=null?`${f1(latest.loss_per_10min_pct)}%`:"-"}/>
            <KV k="Well's average cycle rate (break-even)" v={latest.q_recycle!=null?`${f0(latest.q_recycle)} MCF/d`:"-"}/>
            <KV k="Close rate ÷ break-even" v={latest.q_vs_recycle!=null?`${latest.q_vs_recycle.toFixed(2)}×`:"-"} color={latest.q_vs_recycle!=null&&latest.q_vs_recycle<0.9?C.red:latest.q_vs_recycle>1.1?C.green:C.text}/>
            <KV k={`Next ${f0(params?.horizon_min??10)} minutes: keep vs recycle`} v={latest.v_continue_mcf!=null&&latest.v_recycle_mcf!=null?`${f2(latest.v_continue_mcf)} vs ${f2(latest.v_recycle_mcf)} MCF`:"-"}/>
            <KV k="Break-even crossed at" v={latest.t_cross_min!=null?`${f0(latest.t_cross_min)} min into after-flow`:(fin(latest.q0)&&fin(latest.q_recycle)&&latest.q0<=latest.q_recycle?"Not crossed - below break-even from the start":"-")}/>
            <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, lineHeight:1.7, marginTop:10, background:`${lc}0d`, borderRadius:8, padding:"8px 10px" }}>{latest.explanation}</div>
          </div>
        )}

        <ChartCard title="Flow Rate at Valve Close vs Economic Break-Even Rate"
          subtitle="MCF/d - when the closing rate sits under the well's average cycle rate, recycling pays more than flowing on"
          accentColor={C.red} height={300} delay={0.18}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={plotted} margin={cm}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:{ value:"Cycle", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              {dateXAxis(plotted,"index",5,"Cycle")}
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"MCF/d", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              <Legend {...LEGEND_PROPS}/>
              <Area type="monotone" dataKey="q_close"   name="Closing rate (MCF/d)"          stroke={C.amber} fill={C.amber} fillOpacity={0.12} strokeWidth={2} dot={false} connectNulls/>
              <Area type="monotone" dataKey="q_recycle" name="Break-even / avg cycle rate"   stroke={C.cyan}  fill="none" strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls/>
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="After-Flow Rate Retention at Valve Close"
        subtitle={`% of the reconstructed initial rate still flowing when the valve closed - below ${(depTh*100).toFixed(0)}% = depleted (only cut if it is also below break-even)`}
        accentColor={C.teal} height={260} delay={0.2}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={remData} margin={cm}>
            <defs><linearGradient id="gafrem" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.teal} stopOpacity={0.35}/><stop offset="95%" stopColor={C.teal} stopOpacity={0}/></linearGradient></defs>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:{ value:"Cycle", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
            {dateXAxis(remData,"index",5,"Cycle")}
            <YAxis domain={[0,100]} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} tickFormatter={v=>`${v}%`} label={{ value:"% of initial", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
            <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
            <ReferenceLine y={depTh*100} stroke={C.orange} strokeDasharray="5 3" label={{ value:`depleted < ${(depTh*100).toFixed(0)}%`, fill:C.orange, fontSize:9, fontFamily:MONO, position:"insideTopRight" }}/>
            <Area type="monotone" dataKey="remaining_pct" name="Rate remaining (%)" stroke={C.teal} fill="url(#gafrem)" strokeWidth={2} dot={false}/>
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {topOpp.length>0 && (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"16px 18px", animation:"fade-up 0.5s ease 0.22s both" }}>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text, marginBottom:4 }}>Top After-Flow Optimization Opportunities (Selected Window)</div>
          <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginBottom:10 }}>Cycles where the closing rate was more than 10% under the well's break-even rate. Suggested after-flow durations are in minutes. The time saved is an upper estimate (max 75% of the after-flow, 4 hours per cycle).</div>
          <div style={{ display:"grid", gridTemplateColumns:"minmax(120px,1.1fr) 150px 190px 70px minmax(150px,1.6fr) 24px", gap:10, padding:"6px 0", fontFamily:MONO, fontSize:9, letterSpacing:1, color:C.muted }}>
            <span>CYCLE</span><span>ACTUAL AFTER-FLOW (MINUTES)</span><span title="Suggested after-flow duration to close at, in minutes, shown as shortest (full upper-estimate cut) to the break-even crossing point">SUGGESTED AFTER-FLOW (MINUTES)</span><span>SEVERITY</span><span>CLOSE RATE · CONFIDENCE</span><span/>
          </div>
          {topOpp.map(r=>{
            const sc = C[SEV_COLOR[r.severity]||"muted"];
            return <AfOppItem key={r.index} r={r} sc={sc} horizon={params?.horizon_min??10}/>;
          })}
        </div>
      )}
    </div>
  );
}

export default function AfterFlowPage({ TTip }) {
  const { isDark } = useTheme();         
  applyTheme(getColors(isDark));          
  const { apiUrl } = useWell();

  const [summaryRaw, setSummary]    = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [ratioRaw,   setRatioData]  = useState(null);
  const [prodData,   setProdData]   = useState(null);
  const [cycleEff,   setCycleEff]   = useState(null);
  const [deplData,   setDeplData]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true); setError(null);
      try {
        const [s,ts,ra,pr,ce,dp] = await Promise.all([
          axios.get(apiUrl("/afterflow/summary")),
          axios.get(apiUrl("/afterflow/timeseries?limit=200")),
          axios.get(apiUrl("/afterflow/ratio-analysis")),
          axios.get(apiUrl("/afterflow/production-analysis")),
          axios.get(apiUrl("/afterflow/cycle-efficiency")),
          axios.get(apiUrl("/afterflow/depletion-analysis")).catch(()=>({ data:null })),   // new panel never blocks the rest of the page
        ]);
        setSummary(s.data); setTimeseries(ts.data);
        setRatioData(ra.data); setProdData(pr.data); setCycleEff(ce.data); setDeplData(dp.data);
      } catch(e) { setError(e.message); }
      setLoading(false);
    }
    load();
  }, [apiUrl]);

  /* DATA WINDOW: every cycle in the time-series carries its open time; the window trims what all charts / stats use */
  const tsStamped = useMemo(()=>(timeseries?.data||[]).map(d=>({ ...d, _t:parseT(d.time) })),[timeseries]);
  const tw = useTimeWindow(tsStamped);

  if(loading) return <div style={{ textAlign:"center", padding:80, fontFamily:MONO, fontSize:13, color:C.muted }}>Loading after-flow data...</div>;
  if(error)   return <div style={{ textAlign:"center", padding:80, fontFamily:MONO, fontSize:13, color:C.red }}>Error: {error}</div>;

  const tsData = tw.view;   // "ALL" = every cycle exactly as before
  const vT = tw.view.map(d=>d._t).filter(t=>t!=null);
  const lo = vT.length ? Math.min(...vT) : null, hi = vT.length ? Math.max(...vT) : null;
  const inWin = d => tw.isAll || d._t==null || (lo!=null && d._t>=lo && d._t<=hi);   // other datasets follow the same time span
  const stampRow = d => ({ ...d, _t:parseT(d.time) });
  const cycles  = (cycleEff?.cycles || []).map(stampRow).filter(inWin);
  const prodRows = (prodData?.timeseries || []).map(stampRow).filter(inWin);
  const deplRows = (deplData?.cycles || []).map(stampRow).filter(inWin);
  const hasT = hasStamps(tsData);
  const CM  = hasT ? { ...CHART_MARGIN_DEFAULT, bottom:22 } : CHART_MARGIN_DEFAULT;
  const CMD = hasT ? { ...CHART_MARGIN_DUAL_Y,  bottom:22 } : CHART_MARGIN_DUAL_Y;
  const cycLabel = { value:"Cycle", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO };

  // stat cards / ratio split / velocity categories are re-computed on the windowed cycles (ALL = backend numbers)
  const NON_NEG = ["TimeAfterFlow","VentMin","FlowRateAtClose","VolCycleTotal"];
  const summary = tw.isAll ? summaryRaw : Object.fromEntries(NON_NEG.map(k => [k, statsOf(tsData.map(r => ({ [k]: r[k] >= 0 ? r[k] : null })), k)]));
  const ratioData = tw.isAll ? ratioRaw : ratioOf(prodRows, cycles);

  const ratioHistData = ratioData ? ratioData.bins.map((b,i)=>({
    bin:(Number(b)*100).toFixed(0)+"%", count:ratioData.counts[i], _actualFieldKey:"afterflow_pct"
  })) : [];

  const ratioDonut = ratioData ? [
    { name:"Low AF (<30%)",     value:ratioData.low_afterflow_count },
    { name:"Balanced (30-70%)", value:ratioData.balanced_count },
    { name:"High AF (>70%)",    value:ratioData.high_afterflow_count },
  ] : [];

  const VEL_FLAG = { "Optimal (500-1200 ft/min)":"VelocityOptimal", "Low (<500 ft/min)":"VelocityLow", "High (>1200 ft/min)":"VelocityHigh" };
  const velBreakdown = tw.isAll ? (prodData?.velocity_breakdown || {})
    : Object.fromEntries(Object.keys(prodData?.velocity_breakdown || {}).map(l => [l, prodRows.reduce((a, r) => a + (Number(r[VEL_FLAG[l]]) || 0), 0)]));
  const velTotal = Object.values(velBreakdown).reduce((a,b)=>a+b,0);

  const prodScatter = prodRows.filter(d=>d.TimeAfterFlow!=null&&d.VolCycleTotal!=null);
  const stackAll    = cycles.filter(d=>d.afterflow_pct!=null&&d.shutin_pct!=null);
  const stackData   = tw.isAll ? stackAll.slice(0,80) : stackAll.slice(-80);
  const flowTrend   = tsData.filter(d=>d.FlowRateAtClose!=null);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {tsStamped.some(d=>d._t!=null) && (
        <DataWindowBar ctl={tw} total={tsStamped.length} isDark={isDark}
          note="The window applies to every After-Flow chart, the stat cards, the ratio distribution and the velocity categories."/>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(175px, 1fr))", gap:14 }}>
        <StatCard label="Avg After-Flow" value={summary?.TimeAfterFlow?.mean} unit=" min" color={C.teal}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"/></svg>}
          sub={`Max: ${summary?.TimeAfterFlow?.max?.toFixed(1)??"-"} min`} delay={0}/>
        <StatCard label="Avg Vent Duration" value={summary?.VentMin?.mean} unit=" min" color={C.cyan}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/><path d="M8 19h8"/><path d="M6 22h12"/></svg>}
          sub={`Median: ${summary?.VentMin?.median?.toFixed(1)??"-"} min`} delay={0.05}/>
        <StatCard label="Avg Flow Rate" value={summary?.FlowRateAtClose?.mean} unit=" MCF/d" color={C.amber}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="12" x2="15.5" y2="8.5"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/></svg>}
          sub={`Max: ${summary?.FlowRateAtClose?.max?.toFixed(1)??"-"}`} delay={0.1}/>
        <StatCard label="Avg Cycle Volume" value={summary?.VolCycleTotal?.mean} unit=" MCF" color={C.green}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v12c0 1.66 3.58 3 8 3s8-1.34 8-3V6"/><path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3"/></svg>}
          sub="Total production" delay={0.15}/>
        <StatCard label="After-Flow Ratio" value={ratioData?ratioData.mean*100:null} unit="%" color={C.purple}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="3" x2="12" y2="20"/><path d="M5 20h14"/><path d="M3 7l9-4 9 4"/></svg>}
          sub="% of cycle in after-flow" delay={0.2}/>
      </div>

      <AfterflowDepletionPanel rows={deplRows} basis={deplData?.basis} params={deplData?.params} TTip={TTip}/>

      <ChartCard title="After-Flow & Vent Duration per Cycle" subtitle="TimeAfterFlow = actual production phase  |  VentMin = venting before close" summarySubtitle="minutes of after-flow (left axis) and minutes of vent time before the valve closes (right axis), per cycle" accentColor={C.teal} height={300} delay={0.1}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={tsData} margin={CMD}>
            <defs>
              <linearGradient id="gaftflow" x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor={C.teal} stopOpacity={0.3}/><stop offset="95%" stopColor={C.teal} stopOpacity={0}/></linearGradient>
              <linearGradient id="gvent"    x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor={C.cyan} stopOpacity={0.2}/><stop offset="95%" stopColor={C.cyan} stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:cycLabel}/>
            {dateXAxis(tsData,"index",5,"Cycle")}
            <YAxis yAxisId="left"  tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"min", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
            <YAxis yAxisId="right" orientation="right" tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
            <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
            <Legend {...LEGEND_PROPS}/>
            {tsData[0]?.TimeAfterFlow!=null && <Area yAxisId="left"  type="monotone" dataKey="TimeAfterFlow" name="After-Flow (min)" stroke={C.teal} fill="url(#gaftflow)" strokeWidth={2} dot={false}/>}
            {tsData[0]?.VentMin!=null        && <Area yAxisId="right" type="monotone" dataKey="VentMin"       name="Vent Min"        stroke={C.cyan} fill="url(#gvent)"    strokeWidth={1.5} dot={false}/>}
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:20 }}>
        <ChartCard title="After-Flow Ratio Distribution" subtitle="% of total cycle time spent in after-flow vs shut-in" summarySubtitle="share of each cycle's total time spent flowing after plunger arrival, grouped into bins; number of cycles in each bin" accentColor={C.purple} height={280} delay={0.15}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ratioHistData} margin={CHART_MARGIN_HISTO}>
              <defs><linearGradient id="gratio" x1="0" y1="0" x2="0" y2="1"><stop offset="0%"   stopColor={C.purple} stopOpacity={0.9}/><stop offset="100%" stopColor={C.purple} stopOpacity={0.3}/></linearGradient></defs>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="bin" {...CYCLE_XAXIS_PROPS()} label={{ value:"After-Flow %", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"Cycles", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              <Bar dataKey="count" name="Cycles" fill="url(#gratio)" radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"22px 20px", animation:"fade-up 0.5s ease 0.2s both", display:"flex", flexDirection:"column", gap:16, alignItems:"center", justifyContent:"center" }}>
          {ratioData && <DonutChart data={ratioDonut} colors={[C.purple,C.cyan,C.amber]} title="Cycle Time Split" subtitle={`Total: ${ratioData.total} cycles`}
            summarySubtitle="number of cycles whose after-flow takes a low (under 30%), balanced (30 to 70%) or high (over 70%) share of total cycle time"/>}
          {ratioData && (
            <div style={{ width:"100%", fontFamily:MONO, fontSize:10 }}>
              <InfoRow label="Mean Ratio"   value={(ratioData.mean*100).toFixed(1)}   unit="%" color={C.teal}/>
              <InfoRow label="Median Ratio" value={(ratioData.median*100).toFixed(1)} unit="%" color={C.cyan}/>
            </div>
          )}
        </div>
      </div>

      <ChartCard title="Cycle Time Composition: After-Flow vs Shut-In" subtitle="100% = total cycle time" summarySubtitle="share of each cycle's total time spent in after-flow and in shut-in, per cycle" accentColor={C.amber} height={280} delay={0.2}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={stackData} margin={CM}>
            <defs>
              <linearGradient id="gafstack" x1="0" y1="0" x2="0" y2="1"><stop offset="0%"   stopColor={C.teal} stopOpacity={0.9}/><stop offset="100%" stopColor={C.teal} stopOpacity={0.4}/></linearGradient>
              <linearGradient id="gsistack" x1="0" y1="0" x2="0" y2="1"><stop offset="0%"   stopColor={C.cyan} stopOpacity={0.7}/><stop offset="100%" stopColor={C.cyan} stopOpacity={0.2}/></linearGradient>
            </defs>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:cycLabel}/>
            {dateXAxis(stackData,"index",5,"Cycle")}
            <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} tickFormatter={v=>`${v}%`} label={{ value:"%", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
            <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
            <Legend {...LEGEND_PROPS}/>
            <Bar dataKey="afterflow_pct" name="After-Flow %" stackId="total" fill="url(#gafstack)" radius={[0,0,0,0]}/>
            <Bar dataKey="shutin_pct"    name="Shut-In %"   stackId="total" fill="url(#gsistack)" radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <ChartCard title="Flow Rate at Close: per Cycle" subtitle="MCF/day production rate when valve closes" accentColor={C.amber} height={280} delay={0.25}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={flowTrend} margin={CM}>
              <defs><linearGradient id="gflowrate" x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor={C.amber} stopOpacity={0.4}/><stop offset="95%" stopColor={C.amber} stopOpacity={0}/></linearGradient></defs>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()} height={hasT?20:undefined} label={hasT?undefined:cycLabel}/>
            {dateXAxis(flowTrend,"index",5,"Cycle")}
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"MCF/d", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              {summary?.FlowRateAtClose?.mean!=null && (
                <ReferenceLine y={summary.FlowRateAtClose.mean} stroke={C.amber+"66"} strokeDasharray="5 3"
                  label={{ value:`μ=${summary.FlowRateAtClose.mean.toFixed(1)}`, fill:C.amber, fontSize:9, fontFamily:MONO, position:"insideTopRight" }}/>
              )}
              <Area type="monotone" dataKey="FlowRateAtClose" name="Flow Rate (MCF/d)" stroke={C.amber} fill="url(#gflowrate)" strokeWidth={2} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="After-Flow Duration vs Cycle Volume" subtitle="Does longer after-flow produce more gas?" accentColor={C.green} height={280} delay={0.3}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={CHART_MARGIN_SCATTER}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
              <XAxis dataKey="TimeAfterFlow" name="After-Flow (min)" tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} label={{ value:"After-Flow (min)", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <YAxis dataKey="VolCycleTotal" name="Vol (MCF)"        tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"MCF", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <ZAxis range={[30,30]}/>
              <Tooltip cursor={false} content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              <Scatter data={prodScatter} fill={C.green} fillOpacity={0.65}/>
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {Object.keys(velBreakdown).length > 0 && (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"22px 20px", animation:"fade-up 0.5s ease 0.35s both" }}>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:14, marginBottom:16 }}>Plunger Velocity Categories</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:12 }}>
            {Object.entries(velBreakdown).map(([label,count],i)=>{
              const colors=[C.green,C.amber,C.red];
              const pct=velTotal>0?(count/velTotal*100):0;
              return (
                <div key={label} style={{ background:`${colors[i]}11`, border:`1px solid ${colors[i]}33`, borderRadius:10, padding:"14px 16px" }}>
                  <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginBottom:6 }}>{label}</div>
                  <div style={{ fontFamily:MONO, fontSize:24, fontWeight:700, color:colors[i] }}>{count}</div>
                  <div style={{ height:4, background:C.border, borderRadius:2, marginTop:8 }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:colors[i], borderRadius:2, transition:"width 1.2s ease" }}/>
                  </div>
                  <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:4 }}>{pct.toFixed(1)}% of cycles</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}